import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CATEGORIES, PRODUCTS } from "../../data/products";
import { Reveal, Eyebrow } from "./Reveal";

const PhotoFrame = ({ product }) => {
  const [stage, setStage] = useState(0);
  if (stage === 2 || (stage === 1 && !product.mock)) {
    return (
      <div className="stitch-grid relative flex aspect-[4/3] w-full flex-col items-center justify-center rounded-xl border border-dashed border-[#C88A75]/40 bg-[#EFE6D8]/60 p-6 text-center">
        <span className="font-serif text-6xl font-semibold italic text-[#C88A75]/70">{product.name[0]}</span>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B]">Photo coming soon</p>
        <p className="mt-1 text-xs text-[#6E6259]">Handcrafted on request</p>
      </div>
    );
  }
  const src = stage === 0 ? `/photos/${product.slug}.jpg` : product.mock;
  return (
    <div className="relative">
      <img
        src={src}
        alt={product.name}
        onError={() => setStage((s) => s + 1)}
        data-testid={`product-photo-${product.slug}`}
        className="aspect-[4/3] w-full rounded-xl border border-[#E6DFD5] object-cover"
      />
      {stage === 1 && (
        <span data-testid={`product-mockup-tag-${product.slug}`} className="absolute bottom-2.5 right-2.5 rounded-full bg-[#FDFBF7]/90 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-[#8C7A6B] backdrop-blur">
          Sample mockup
        </span>
      )}
    </div>
  );
};

export default function Products({ onRequest }) {
  const [active, setActive] = useState("All");
  const visible = active === "All" ? PRODUCTS : PRODUCTS.filter((p) => p.category === active);

  return (
    <section id="collection" data-testid="collection-section" className="bg-blend-oat py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow testId="collection-eyebrow">The Collection</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl font-medium leading-[1.15] tracking-tight text-[#2C2520] sm:text-4xl lg:text-5xl">
              Made to order, <span className="italic text-[#A35C45]">priced to start.</span>
            </h2>
          </div>
          <p className="max-w-xs text-sm leading-relaxed text-[#6E6259]">
            Final price depends on size &amp; design — these give you a real number to start from.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-2.5" role="tablist" aria-label="Filter products">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                role="tab"
                aria-selected={active === cat}
                data-testid={`category-tab-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setActive(cat)}
                className={`rounded-full px-5 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                  active === cat ? "bg-[#2C2520] text-[#FDFBF7]" : "border border-[#2C2520]/20 text-[#6E6259] hover:border-[#A35C45] hover:text-[#A35C45]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-12 grid gap-6 sm:grid-cols-2 sm:gap-8 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {visible.map((p, i) => (
              <motion.article
                layout
                key={p.slug}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.5, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`product-card-${p.slug}`}
                className="group rounded-2xl border border-[#E6DFD5] bg-[#FDFBF7] p-5 transition-shadow duration-500 hover:shadow-[0_24px_50px_-20px_rgba(44,37,32,0.2)]"
              >
                <div className="overflow-hidden rounded-xl transition-transform duration-500 group-hover:scale-[1.015]">
                  <PhotoFrame product={p} />
                </div>
                <div className="mt-5 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-serif text-2xl font-medium tracking-tight text-[#2C2520]">{p.name}</h3>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C7A6B]">{p.sizes}</p>
                  </div>
                  <span data-testid={`product-price-${p.slug}`} className="shrink-0 rounded-full bg-[#EFE6D8] px-3.5 py-1.5 text-sm font-semibold text-[#5C4D42]">
                    {p.price}
                  </span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-[#6E6259]">{p.blurb}</p>
                <button
                  onClick={() => onRequest(p.name)}
                  data-testid={`product-request-${p.slug}`}
                  className="mt-5 w-full rounded-full border border-[#2C2520]/20 py-2.5 text-sm font-medium text-[#2C2520] transition-colors duration-300 hover:border-[#A35C45] hover:bg-[#A35C45] hover:text-[#FDFBF7]"
                >
                  Request this piece
                </button>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
